import { useMemo } from 'react';
import { sprintf } from 'sprintf-js';

import { Box } from 'components/Box';
import { Icon } from 'components/Icon';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import {
  NOTIFICATIONS,
  NotificationStatus,
  useLocalNotifications,
} from 'utils/localNotifications';
import { hasResumeDb, promoteResumeDatabase } from 'utils/resumeDatabase';

import { StaffPageHeaderAccountDropdownProfileSection } from './StaffPageHeaderAccountDropdownProfileSection';
import {
  PageHeaderDropdownMenuItem,
  StaffPageHeaderDropdownMenu,
  StaffPageHeaderDropdownMenuSection,
  StaffPageHeaderDropdownMenuSectionTitle,
} from './StaffPageHeaderDropdownMenu';

const NAV_WIDTH = {
  en: 900,
  es: 1050,
  pt: 1000,
};

export function StaffPageHeaderAccountDropdown() {
  const userEnvironment = useUserEnvironment();

  const { user } = userEnvironment;

  const { localNotifications } = useLocalNotifications();

  const resumeNotification = useMemo(
    () =>
      localNotifications?.find(
        (notification) => notification.type === NOTIFICATIONS.UPLOAD_RESUME,
      ),
    [localNotifications],
  );

  const isAdmin = (user?.orgs?.length || 0) > 0;

  const promoteResumeDb = useMemo(
    () =>
      resumeNotification?.status === NotificationStatus.NEW &&
      promoteResumeDatabase({ userEnvironment }),
    [resumeNotification, userEnvironment],
  );

  return (
    <Box position="relative">
      <StaffPageHeaderDropdownMenu
        height="calc(100vh - 80px)"
        right
        aria-label={getText('Account Information')}
        title={
          <Box
            display={['none', null, null, 'flex']}
            alignItems="center"
            maxWidth={[
              null,
              null,
              null,
              `calc(100vw - ${NAV_WIDTH[CURRENT_LOCALE]}px)`,
            ]}
            minWidth={[null, null, null, 32]}
          >
            <Box whiteSpace="noWrap" textOverflow="ellipsis" overflow="hidden">
              {
                /* TRANSLATORS: user greeting at top of page */
                sprintf(getText('Hi, %(name)s!'), {
                  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                  name: user.firstName || user.email,
                })
              }
            </Box>
          </Box>
        }
        data-qa-id="nav-dropdown"
        onClick={() => {
          trackClicked('Header Utility CTA - Hi, Name');
        }}
      >
        {({ closeMenu }) => (
          <>
            {promoteResumeDb ? (
              <StaffPageHeaderAccountDropdownProfileSection
                closeMenu={closeMenu}
              />
            ) : null}
            {
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              user.orgs.length > 0 && (
                <StaffPageHeaderDropdownMenuSection $noBorder>
                  <StaffPageHeaderDropdownMenuSectionTitle>
                    {getText('Manage')}
                  </StaffPageHeaderDropdownMenuSectionTitle>
                  {
                    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                    user.orgs.map((org) => (
                      <PageHeaderDropdownMenuItem
                        key={org.id}
                        href={
                          org.hasGradProgram && !org.overrideToMainDashboard
                            ? `/admin/${org.id}/grad-program`
                            : `/admin/${org.id}`
                        }
                        onClick={() => {
                          trackClicked('Header Dropdown Link', {
                            link_type: 'Organization',
                            url:
                              org.hasGradProgram && !org.overrideToMainDashboard
                                ? `/admin/${org.id}/grad-program`
                                : `/admin/${org.id}`,
                          });
                          closeMenu();
                        }}
                      >
                        <span className="truncate">{org.name}</span>
                      </PageHeaderDropdownMenuItem>
                    ))
                  }
                </StaffPageHeaderDropdownMenuSection>
              )
            }

            <StaffPageHeaderDropdownMenuSection $noBorder={!isAdmin}>
              <StaffPageHeaderDropdownMenuSectionTitle>
                {getText('My Account')}
              </StaffPageHeaderDropdownMenuSectionTitle>

              <PageHeaderDropdownMenuItem
                data-qa-id="page-header-account-dropdown-saved-items"
                href="/dashboard/saved-items"
                onClick={() => {
                  trackClicked('Header Dropdown Link', {
                    link_type: 'My Saved Items',
                    url: '/dashboard/saved-items',
                  });
                  closeMenu();
                }}
              >
                <Icon name="bookmark" size={16} />
                <Box display="inline-block" ml={2}>
                  {getText('My Saved Items')}
                </Box>
              </PageHeaderDropdownMenuItem>

              {hasResumeDb(userEnvironment) ? (
                <PageHeaderDropdownMenuItem
                  data-qa-id="page-header-account-dropdown-employment-profile"
                  href="/dashboard/employment-profile"
                  onClick={() => {
                    trackClicked('Header Dropdown Link', {
                      link_type: 'Employment profile',
                      url: '/dashboard/employment-profile',
                    });
                    closeMenu();
                  }}
                >
                  <Icon name="person" size={16} />
                  <Box display="inline-block" ml={2}>
                    {getText('Profile')}
                  </Box>
                </PageHeaderDropdownMenuItem>
              ) : null}

              <PageHeaderDropdownMenuItem
                data-qa-id="page-header-account-dropdown-account-settings"
                href="/dashboard/account-settings"
                onClick={() => {
                  trackClicked('Header Dropdown Link', {
                    link_type: 'Settings',
                    url: '/dashboard/account-settings',
                  });
                  closeMenu();
                }}
              >
                <Icon name="gear" size={16} />
                <Box display="inline-block" ml={2}>
                  {getText('Settings')}
                </Box>
              </PageHeaderDropdownMenuItem>
            </StaffPageHeaderDropdownMenuSection>

            <StaffPageHeaderDropdownMenuSection>
              <PageHeaderDropdownMenuItem
                href="/logout"
                data-qa-id="logout-link"
                $logoutLink
              >
                {getText('Log Out')}
              </PageHeaderDropdownMenuItem>
            </StaffPageHeaderDropdownMenuSection>
          </>
        )}
      </StaffPageHeaderDropdownMenu>
    </Box>
  );
}
